import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Modal,
  Input,
  Select,
  Button,
  Table,
  Avatar,
  notification,
} from "antd";
import { LuFilter, LuSearch, LuX } from "react-icons/lu";
import { useDebounce } from "use-debounce";
import moment from "moment";
import {
  useGetInterpretersQuery,
  useUnassignInterpreterMutation,
} from "../../store/api/apiSlice";
import { buildConditionalPagination } from "../../utils/pagination";
import { useInterpreterAssignment } from "../../hooks/useInterpreterAssignment";
import {
  getApiErrorMessage,
  getAssignmentOrganizationId,
  getAssignmentQuestionId,
  isInterpreterAlreadyAssigned,
  resolveInterpreterId,
} from "../../utils/interpreterAssignment";

const AssignInterpreterModal = ({
  visible,
  onClose,
  onSuccess,
  questionId,
  organizationId: initialOrgId,
  isSecondCheck = false,
  assignment,
  videoRefs,
}) => {
  const [modalState, setModalState] = useState({
    organizationId: initialOrgId,
    questionId,
  });
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSortBy] = useState("newest");
  const [debouncedValue] = useDebounce(searchValue, 500);
  const { data: interpretersData, isLoading } = useGetInterpretersQuery(
    undefined,
    {
      skip: !visible,
    }
  );

  const { assignInterpreterToRecord } = useInterpreterAssignment();
  const [unassignInterpreter] = useUnassignInterpreterMutation();

  const assignmentRecord = useMemo(() => {
    if (assignment) return assignment;

    if (questionId || initialOrgId) {
      return {
        question: questionId ? { _id: questionId } : undefined,
        organization: initialOrgId ? { _id: initialOrgId } : undefined,
      };
    }

    return null;
  }, [assignment, questionId, initialOrgId]);

  const resolvedQuestionId =
    questionId || getAssignmentQuestionId(assignmentRecord);
  const isVideoAssignmentRecord = Boolean(assignment?._id);

  const isInterpreterAssigned = (interpreter) => {
    const interpreterId = resolveInterpreterId(interpreter);
    
    if (!interpreterId) return false;

    if (isVideoAssignmentRecord) {
      return (
        isInterpreterAlreadyAssigned(assignmentRecord, interpreterId) ||
        isInterpreterAlreadyAssigned(
          assignmentRecord,
          interpreter?.user?._id,
        )
      );
    }

    return interpreter.assignments?.some(
      (item) => item.question?._id === resolvedQuestionId,
    );
  };

  const getAssignmentId = (interpreter) => {
    if (isVideoAssignmentRecord && isInterpreterAssigned(interpreter)) {
      return assignmentRecord?._id;
    }

    if (isVideoAssignmentRecord) return null;

    return interpreter.assignments?.find(
      (item) => item.question?._id === resolvedQuestionId,
    )?._id;
  };

  useEffect(() => {
    setModalState((prev) => ({
      ...prev,
      organizationId: initialOrgId,
      questionId,
    }));
  }, [initialOrgId, questionId]);

  const handleAssign = useCallback(
    async (interpreterRecord) => {
      const interpreterId = resolveInterpreterId(interpreterRecord);
      const organizationId =
        getAssignmentOrganizationId(assignmentRecord) || modalState.organizationId;

      if (!interpreterId) {
        notification.error({
          message: "Error",
          description: "Interpreter not found",
        });
      
        return;
      }

      if (!organizationId) {
        notification.error({
          message: "Error",
          description: "Organisation ID is required for assignment",
        });
      
        return;
      }

      try {
        const result = await assignInterpreterToRecord({
          assignment:
            assignmentRecord ||
            ({
              question: resolvedQuestionId
                ? { _id: resolvedQuestionId }
                : undefined,
              organization: { _id: organizationId },
            }),
          interpreterId,
          interpreter: interpreterRecord,
          isSecondCheck,
          videoRefs: videoRefs || { current: {} },
        });

        notification.success({
          message: "Success",
          description: isSecondCheck
            ? "Second check interpreter assigned successfully"
            : "Interpreter assigned successfully",
        });
        onSuccess?.({
          interpreterId,
          assignmentId: result?.assignmentId || assignmentRecord?._id,
        });
        onClose();
      } catch (error) {
        notification.error({
          message: "Failed to assign video",
          description: getApiErrorMessage(
            error,
            "The assignment request was rejected. Check the interpreter and video details and try again.",
          ),
        });
      }
    },
    [
      assignmentRecord,
      modalState.organizationId,
      resolvedQuestionId,
      assignInterpreterToRecord,
      isSecondCheck,
      onClose,
      onSuccess,
      videoRefs,
    ],
  );
  // Create the click handler with the stored organizationId
  const createAssignHandler = useCallback(
    (interpreterRecord) => () => {
      handleAssign(interpreterRecord);
    },
    [handleAssign],
  );

  const handleUnassign = async (assignmentId) => {
    try {
      await unassignInterpreter(assignmentId).unwrap();
      notification.success({
        message: "Success",
        description: "Interpreter unassigned successfully",
      });
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.data?.message || "Failed to unassign interpreter",
      });
    }
  };

  const mappedData = useMemo(
    () =>
      interpretersData?.data?.map((interpreter) => ({
        key: interpreter._id,
        _id: interpreter._id,
        name: interpreter.user?.full_name,
        email: interpreter.user?.email,
        image: interpreter.image,
        status: interpreter.status,
        experience: interpreter.experience,
        joinedDate: moment(interpreter.date_created).format("DD MMM YYYY"),
        assignments: interpreter.assignments || [],
        totalAssignments: interpreter.assignments?.length || 0,
        completedAssignments:
          interpreter.assignments?.filter((a) => a.status === "complete")
            ?.length || 0,
        isAssigned: isInterpreterAssigned(interpreter),
        assignmentId: getAssignmentId(interpreter),
      })) || [],
    [interpretersData, resolvedQuestionId, assignmentRecord, isVideoAssignmentRecord]
  );

  const filteredInterpreters = useMemo(() => {
    let filtered = [...mappedData];

    // Always exclude inactive interpreters from assignment selection
    filtered = filtered.filter(
      (item) => item.status?.toLowerCase() !== "inactive"
    );

    if (debouncedValue) {
      filtered = filtered.filter(
        (item) =>
          item.name?.toLowerCase().includes(debouncedValue.toLowerCase()) ||
          item.email?.toLowerCase().includes(debouncedValue.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (item) => item.status?.toLowerCase() === statusFilter
      );
    }

    filtered.sort((a, b) => {
      if (sort === "newest") {
        return moment(b.joinedDate).valueOf() - moment(a.joinedDate).valueOf();
      }
      return moment(a.joinedDate).valueOf() - moment(b.joinedDate).valueOf();
    });

    return filtered;
  }, [mappedData, debouncedValue, statusFilter, sort]);

  const columns = [
    {
      title: "Sr#",
      key: "index",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Interpreter",
      key: "interpreter",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <Avatar src={record.image} alt={record.name} />
          <div>
            <div className="font-medium">{record.name}</div>
            <div className="text-sm text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Assignments",
      key: "assignments",
      width: 120,
      render: (_, record) =>
        `${record.completedAssignments}/${record.totalAssignments}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <span
          className={`px-3 py-2 rounded-full text-sm ${
            status?.toLowerCase() === "active"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: 100,
      render: (exp) => `${exp || 0} years`,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          className={`h-9 text-sm rounded-full border-0 ${
            record.isAssigned
              ? "bg-red-100 text-red-600 hover:bg-red-200"
              : "bg-blue-100 text-blue-600 hover:bg-blue-200"
          }`}
          onClick={() =>
            record.isAssigned
              ? handleUnassign(record.assignmentId)
              : handleAssign(record)
          }
        >
          {record.isAssigned ? "Unassign" : "Assign"}
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      closeIcon={
        <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      title={<h3 className="text-xl font-semibold mt-0">Assign Interpreter</h3>}
      className="interpreter-modal"
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1">
            <Input
              placeholder="Search interpreters..."
              onChange={(e) => setSearchValue(e.target.value)}
              className="h-11 rounded-xl"
              prefix={<LuSearch className="text-gray-400" />}
            />
          </div>

          <Select
            defaultValue="newest"
            className="w-40 h-11"
            onChange={(value) => setSortBy(value)}
            options={[
              { value: "newest", label: "Newest First" },
              { value: "oldest", label: "Oldest First" },
            ]}
          />

          <Select
            defaultValue="all"
            className="w-40 h-11"
            onChange={(value) => setStatusFilter(value)}
            options={[
              { value: "all", label: "All Status" },
              { value: "active", label: "Active" },
            ]}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredInterpreters}
          loading={isLoading}
          pagination={buildConditionalPagination(filteredInterpreters)}
          scroll={{ x: 800 }}
          className="rounded-xl border"
        />
      </div>
    </Modal>
  );
};

export default AssignInterpreterModal;
